/**
 * S020-8.20.5: ReviewService
 *
 * Manages pending decisions and activity stats for automation review.
 *
 * Backend Endpoints:
 * - GET /api/workspace/:workspaceId/workflow/pending-decisions
 * - POST /api/workspace/:workspaceId/workflow/pending-decisions/:id/resolve
 * - POST /api/workspace/:workspaceId/workflow/pending-decisions/:id/skip
 * - GET /api/workspace/:workspaceId/workflow/activity
 */

import { LiveData, Service } from '@toeverything/infra';

import { defaultWorkspaceApiClient } from '../../cloud';
import { workflowWorkspaceRoutes } from './workflow-workspace-routes';

// =============================================================================
// Types
// =============================================================================

/**
 * Candidate row for relation lookup disambiguation
 */
export interface RelationCandidate {
  id: string;
  title: string;
  context?: string; // Additional info like "12 previous invoices"
}

/**
 * Status of a pending decision
 */
export type PendingDecisionStatus = 'pending' | 'resolved' | 'skipped';

/**
 * A pending decision that needs user input
 */
export interface PendingDecision {
  id: string;
  triggerId: string;
  configId: string;
  executionLogId: string;
  sourceRowId: string;
  targetColumn: string;
  targetColumnName: string | null;
  searchValue: string;
  matchCount: number;
  candidates: RelationCandidate[];
  status: PendingDecisionStatus;
  resolvedWith: string | null;
  resolvedAt: string | null;
  resolvedBy: string | null;
  createdAt: string;
  /** Block ID of the automation config (for linking to workflow) */
  blockId?: string;
  /** Doc ID where the automation lives */
  docId?: string;
  /** Human-readable doc title */
  docTitle?: string | null;
  /** Human-readable automation block name */
  automationName?: string | null;
}

/**
 * Daily activity stats
 */
export interface DailyActivity {
  date: string;
  runs: number;
  issues: number;
}

/**
 * Activity stats response
 */
export interface ActivityStats {
  daily: DailyActivity[];
  totalRuns: number;
  successRate: number;
  pendingCount: number;
}

// =============================================================================
// ReviewService
// =============================================================================

export class ReviewService extends Service {
  // Observable state
  private readonly _pendingDecisions$ = new LiveData<PendingDecision[]>([]);
  private readonly _activityStats$ = new LiveData<ActivityStats | null>(null);
  private readonly _isLoading$ = new LiveData<boolean>(false);
  private readonly _error$ = new LiveData<string | null>(null);

  private request(
    workspaceId: string,
    path: string,
    init: RequestInit = {}
  ): Promise<Response> {
    const method = init.method ?? 'GET';
    const isMutation = method !== 'GET';
    return defaultWorkspaceApiClient.request({
      workspaceId,
      path,
      init,
      category: isMutation ? 'foreground-action' : 'workspace-api',
      key: `${method}:${path}`,
      mode: isMutation ? 'foreground' : 'background',
      priority: isMutation ? 'foreground' : 'visible',
    });
  }

  // Public LiveData (useLiveData expects LiveData, not RxJS Observable)
  readonly pendingDecisions$ = this._pendingDecisions$;
  readonly activityStats$ = this._activityStats$;
  readonly isLoading$ = this._isLoading$;
  readonly error$ = this._error$;

  // ==========================================================================
  // Pending Decisions
  // ==========================================================================

  /**
   * Fetch pending decisions for a workspace
   */
  async fetchPendingDecisions(workspaceId: string): Promise<void> {
    this._isLoading$.next(true);
    this._error$.next(null);

    try {
      const response = await this.request(
        workspaceId,
        workflowWorkspaceRoutes.pendingDecisions(workspaceId)
      );

      if (!response.ok) {
        throw new Error(
          `Failed to fetch pending decisions: ${response.status}`
        );
      }

      const data: PendingDecision[] = await response.json();
      this._pendingDecisions$.next(data);
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Unknown error';
      this._error$.next(message);
      console.error('[ReviewService] fetchPendingDecisions error:', error);
    } finally {
      this._isLoading$.next(false);
    }
  }

  /**
   * Resolve a pending decision by selecting a candidate
   */
  async resolveDecision(
    workspaceId: string,
    decisionId: string,
    resolvedWith: string
  ): Promise<boolean> {
    try {
      const response = await this.request(
        workspaceId,
        workflowWorkspaceRoutes.resolveDecision(workspaceId, decisionId),
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ resolvedWith }),
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to resolve decision: ${response.status}`);
      }

      const resolved: PendingDecision = await response.json();
      this.applyDecision(decisionId, resolved);

      return true;
    } catch (error) {
      console.error('[ReviewService] resolveDecision error:', error);
      return false;
    }
  }

  /**
   * Skip a pending decision (leave unlinked)
   */
  async skipDecision(
    workspaceId: string,
    decisionId: string
  ): Promise<boolean> {
    try {
      const response = await this.request(
        workspaceId,
        workflowWorkspaceRoutes.skipDecision(workspaceId, decisionId),
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to skip decision: ${response.status}`);
      }

      const skipped: PendingDecision = await response.json();
      this.applyDecision(decisionId, skipped);

      return true;
    } catch (error) {
      console.error('[ReviewService] skipDecision error:', error);
      return false;
    }
  }

  /**
   * Replace a decision with the server's answer. A decision that leaves
   * 'pending' also leaves the activity stats' pendingCount, which the
   * workspace automations panel shows as its section title and visibility.
   */
  private applyDecision(decisionId: string, updated: PendingDecision): void {
    const decisions = this._pendingDecisions$.value;
    const wasPending =
      decisions.find(d => d.id === decisionId)?.status === 'pending';
    this._pendingDecisions$.next(
      decisions.map(d => (d.id === decisionId ? updated : d))
    );
    const stats = this._activityStats$.value;
    if (wasPending && updated.status !== 'pending' && stats) {
      this._activityStats$.next({
        ...stats,
        pendingCount: Math.max(0, stats.pendingCount - 1),
      });
    }
  }

  /**
   * Get count of pending decisions
   */
  get pendingCount(): number {
    return this._pendingDecisions$.value.filter(d => d.status === 'pending')
      .length;
  }

  // ==========================================================================
  // Activity Stats
  // ==========================================================================

  /**
   * Fetch activity stats for a workspace
   */
  async fetchActivityStats(
    workspaceId: string,
    days: number = 30
  ): Promise<void> {
    this._isLoading$.next(true);
    this._error$.next(null);

    try {
      const response = await this.request(
        workspaceId,
        workflowWorkspaceRoutes.activity(workspaceId, days)
      );

      if (!response.ok) {
        throw new Error(`Failed to fetch activity stats: ${response.status}`);
      }

      const data: ActivityStats = await response.json();
      this._activityStats$.next(data);
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Unknown error';
      this._error$.next(message);
      console.error('[ReviewService] fetchActivityStats error:', error);
    } finally {
      this._isLoading$.next(false);
    }
  }

  /**
   * Refresh all data for a workspace
   */
  async refreshAll(workspaceId: string): Promise<void> {
    await Promise.all([
      this.fetchPendingDecisions(workspaceId),
      this.fetchActivityStats(workspaceId),
    ]);
  }

  /**
   * Clear all data
   */
  clear(): void {
    this._pendingDecisions$.next([]);
    this._activityStats$.next(null);
    this._error$.next(null);
  }
}
